import React, { ReactNode } from 'react'

import {
  getAllowanceByFamilyStatus,
  getCostSharingAmountByFamilyStatus,
} from 'adminDashboard/benefitDetails/common/ProductClassHelpers'
import classNames from 'classnames'

import { ClassType } from 'core/application/adminDashboard/benefitDetails/design/productClass/selectClass'
import AllowanceType from 'core/domain/benefit/allowanceType'
import { ProductClass } from 'core/domain/benefit/productClass'
import { FamilyStatus } from 'core/domain/companies/familyStatus'

import Icon from 'common/components/general/Icon'

import { Col, Row, Skeleton } from 'common/components'
import { formatMoney } from 'common/moneyFormatter'

import { ProductClassModalStateProps } from './index'
import { ProductClassStep } from './productClassWizard/ProductClassStepsMapping'

const editIcon = (
  setProductClassModalState: (state: ProductClassModalStateProps) => void,
  productClassId: string | null,
  step: ProductClassStep,
): ReactNode => (
  <h6
    onClick={() => {
      setModalState(setProductClassModalState, productClassId, step)
    }}
    className="pointer edit_link"
  >
    <Icon type="far fa-pencil-alt" fontAwesome />
  </h6>
)

export const productClassSummary = (
  item: ProductClass | null,
  setProductClassModalState: (state: ProductClassModalStateProps) => void,
  translate: (key: string) => string,
): ReactNode => {
  return (
    <Skeleton active loading={item === null}>
      {item !== null ? (
        <Row>
          {getClassTypeContent(item, setProductClassModalState, translate)}
          {getAllowanceContent(item, setProductClassModalState, translate)}
          {getEobContent(item, setProductClassModalState, translate)}
          {getCostSharingAmountContent(item, setProductClassModalState, translate)}
          {getCostSharingPercentageContent(item, setProductClassModalState, translate)}
          {getWaitingPeriodContent(item, setProductClassModalState, translate)}
          {getProrateAllowanceContent(item, setProductClassModalState, translate)}
        </Row>
      ) : null}
    </Skeleton>
  )
}

const getClassTypeContent = (
  item: ProductClass,
  setProductClassModalState: (state: ProductClassModalStateProps) => void,
  translate: (key: string) => string,
): ReactNode => {
  return item.classType?.id !== ClassType.AllEligibleEmployees ? (
    <Col span={24} className="class_section p-8">
      <div className="class_section_header border_bottom">
        <h6 className="title">{translate('admin-dashboard.benefit-details.summary.class-employee')}</h6>
        {editIcon(setProductClassModalState, item.id, ProductClassStep.SelectClass)}
      </div>
      <div className="content">{item.name}</div>
    </Col>
  ) : null
}

const getAllowanceContent = (
  item: ProductClass,
  setProductClassModalState: (state: ProductClassModalStateProps) => void,
  translate: (key: string) => string,
): ReactNode => {
  return (
    <Col
      span={24}
      className={classNames({
        class_section: true,
        'p-8': true,
        'pt-24': item.classType?.id !== ClassType.AllEligibleEmployees,
      })}
    >
      <div className="class_section_header border_bottom">
        <h6 className="title">{translate('admin-dashboard.benefit-details.summary.class-allowance')}</h6>
        {allowanceHeaderValue(item, setProductClassModalState, translate)}
      </div>
      {item.offerSameAllowance !== null && (
        <div className="class_section_content">
          {item.offerSameAllowance ? sameAllowance(item, translate) : differentAllowance(item, translate)}
        </div>
      )}
    </Col>
  )
}

const allowanceHeaderValue = (
  item: ProductClass,
  setProductClassModalState: (state: ProductClassModalStateProps) => void,
  translate: (key: string) => string,
): ReactNode => {
  if (item.offerSameAllowance === null) return notSetHeaderValue(translate)
  return editIcon(setProductClassModalState, item.id, ProductClassStep.SetAllowance)
}

const sameAllowance = (item: ProductClass, translate: (key: string) => string): ReactNode => {
  return (
    <Row className="pt-8">
      <Col span={12}>
        <label className="option">
          {item.allowanceType === AllowanceType.Annual
            ? translate('admin-dashboard.benefit-details.summary.labels.annual-allowance')
            : translate('admin-dashboard.benefit-details.summary.labels.monthly-allowance')}
          : {formatMoney(item.familyAllowances[0].amount)}
          {/*: {formatMoney(item.productClassAllowance[0].amount)}*/}
        </label>
      </Col>
    </Row>
  )
}

const differentAllowance = (item: ProductClass, translate: (key: string) => string): ReactNode => {
  return (
    <>
      <Row className="pt-8">
        <Col span={12}>
          <label className="option">
            {translate('admin-dashboard.benefit-details.summary.labels.family-status-single')}:{' '}
            {getAllowanceByFamilyStatus(item, FamilyStatus.Single)}
          </label>
        </Col>
        <Col span={12}>
          <label className="option">
            {translate('admin-dashboard.benefit-details.summary.labels.family-status-married')}:{' '}
            {getAllowanceByFamilyStatus(item, FamilyStatus.Married)}
          </label>
        </Col>
      </Row>
      <Row className="pt-8">
        <Col span={12}>
          <label className="option">
            {translate('admin-dashboard.benefit-details.summary.labels.family-status-single-dep')}:{' '}
            {getAllowanceByFamilyStatus(item, FamilyStatus.SingleWithDependents)}
          </label>
        </Col>
        <Col span={12}>
          <label className="option">
            {translate('admin-dashboard.benefit-details.summary.labels.family-status-married-dep')}:{' '}
            {getAllowanceByFamilyStatus(item, FamilyStatus.MarriedWithDependents)}
          </label>
        </Col>
      </Row>
    </>
  )
}

const getEobContent = (
  item: ProductClass,
  setProductClassModalState: (state: ProductClassModalStateProps) => void,
  translate: (key: string) => string,
): ReactNode => {
  return (
    <Col span={24} className="class_section p-8 pt-24">
      <div className="class_section_header border_bottom">
        <h6 className="title">
          {translate('admin-dashboard.benefit-details.summary.product-class-summary-eob-requirement')}:{' '}
          {item.requiresEOB === null ? '' : item.requiresEOB ? 'Yes' : 'No'}
        </h6>
        {eobHeaderValue(item, setProductClassModalState, translate)}
      </div>
      {getExemptFromEobValues(item, translate)}
    </Col>
  )
}

const getExemptFromEobValues = (item: ProductClass, translate: (key: string) => string): ReactNode => {
  if (item.requiresEOB && item.eobExpense && item.eobExpense.length > 0) {
    return (
      <div className="class_section_content">
        <Row className="pt-8">
          <Col span={12}>
            <label className="option">
              {translate('admin-dashboard.benefit-details.summary.exempt-from-eob')}:{' '}
              {item.eobExpense.map((item: any) => item.name).join(', ')}
              {item.requiresPrescription
                ? `, ${translate('admin-dashboard.benefit-details.summary.prescription-label')}`
                : ''}
            </label>
          </Col>
        </Row>
      </div>
    )
  } else if (item.requiresEOB && (!item.eobExpense || item.eobExpense.length === 0) && item.requiresPrescription) {
    return (
      <div className="class_section_content">
        <Row className="pt-8">
          <Col span={12}>
            <label className="option">
              {translate('admin-dashboard.benefit-details.summary.exempt-from-eob')}:{' '}
              {translate('admin-dashboard.benefit-details.summary.prescription-label')}
            </label>
          </Col>
        </Row>
      </div>
    )
  } else {
    return null
  }
}

const eobHeaderValue = (
  item: ProductClass,
  setProductClassModalState: (state: ProductClassModalStateProps) => void,
  translate: (key: string) => string,
): ReactNode => {
  if (item.requiresEOB === null) return notSetHeaderValue(translate)

  return editIcon(setProductClassModalState, item.id, ProductClassStep.ExpenseReimbursement)
}

const getCostSharingAmountContent = (
  item: ProductClass,
  setProductClassModalState: (state: ProductClassModalStateProps) => void,
  translate: (key: string) => string,
): ReactNode => {
  return (
    <Col span={24} className="class_section p-8 pt-24">
      <div className="class_section_header border_bottom">
        <h6 className="title">
          {translate('admin-dashboard.benefit-details.summary.class-hra-deductible')}:{' '}
          {item.deductible === null ? '' : item.deductible ? 'Yes' : 'No'}
        </h6>
        {costSharingAmountHeaderValue(item, setProductClassModalState, translate)}
      </div>
      {item.deductible !== null && item.deductible ? (
        <div className="class_section_content">
          {item.offerSameAllowance !== null && item.offerSameAllowance
            ? sameCostSharingAmount(item, translate)
            : differentCostSharingAmount(item, translate)}
        </div>
      ) : null}
    </Col>
  )
}

const costSharingAmountHeaderValue = (
  item: ProductClass,
  setProductClassModalState: (state: ProductClassModalStateProps) => void,
  translate: (key: string) => string,
): ReactNode => {
  if (item.deductible === null) return notSetHeaderValue(translate)

  return editIcon(setProductClassModalState, item.id, ProductClassStep.CostSharingAmount)
}

const sameCostSharingAmount = (item: ProductClass, translate: (key: string) => string): ReactNode => {
  return (
    <Row className="pt-8">
      <Col span={12}>
        <label className="option">
          {translate('admin-dashboard.benefit-details.summary.labels.deductible-amount')}:{' '}
          {formatMoney(item.familyDeductibles[0].deductible)}
        </label>
      </Col>
    </Row>
  )
}

const differentCostSharingAmount = (item: ProductClass, translate: (key: string) => string): ReactNode => {
  return (
    <>
      <Row className="pt-8">
        <Col span={12}>
          <label className="option">
            {translate('admin-dashboard.benefit-details.summary.labels.family-status-single')}:{' '}
            {getCostSharingAmountByFamilyStatus(item, FamilyStatus.Single)}
          </label>
        </Col>
        <Col span={12}>
          <label className="option">
            {translate('admin-dashboard.benefit-details.summary.labels.family-status-married')}:{' '}
            {getCostSharingAmountByFamilyStatus(item, FamilyStatus.Married)}
          </label>
        </Col>
      </Row>
      <Row className="pt-8">
        <Col span={12}>
          <label className="option">
            {translate('admin-dashboard.benefit-details.summary.labels.family-status-single-dep')}:{' '}
            {getCostSharingAmountByFamilyStatus(item, FamilyStatus.SingleWithDependents)}
          </label>
        </Col>
        <Col span={12}>
          <label className="option">
            {translate('admin-dashboard.benefit-details.summary.labels.family-status-married-dep')}:{' '}
            {getCostSharingAmountByFamilyStatus(item, FamilyStatus.MarriedWithDependents)}
          </label>
        </Col>
      </Row>
    </>
  )
}

const getCostSharingPercentageContent = (
  item: ProductClass,
  setProductClassModalState: (state: ProductClassModalStateProps) => void,
  translate: (key: string) => string,
): ReactNode => {
  return (
    <Col span={24} className="class_section p-8 pt-24">
      <div className="class_section_header border_bottom">
        <h6 className="title">
          {translate('admin-dashboard.benefit-details.summary.class-cost-sharing')}:{' '}
          {item.eeCoinsurance === null ? '' : item.eeCoinsurance ? 'Yes' : 'No'}
        </h6>
        {costSharingPercentageHeaderValue(item, setProductClassModalState, translate)}
      </div>

      {item.eeCoinsurance !== null && item.eeCoinsurance ? (
        <div className="class_section_content">
          <Row className="pt-8">
            <Col span={12}>
              <label className="option">
                {translate('admin-dashboard.benefit-details.summary.class-cost-sharing-2')}: {item.eeCoinsuranceAmount}%
              </label>
            </Col>
          </Row>
        </div>
      ) : null}
    </Col>
  )
}

const costSharingPercentageHeaderValue = (
  item: ProductClass,
  setProductClassModalState: (state: ProductClassModalStateProps) => void,
  translate: (key: string) => string,
): ReactNode => {
  if (item.eeCoinsurance === null) return notSetHeaderValue(translate)

  return editIcon(setProductClassModalState, item.id, ProductClassStep.CostSharingPercentage)
}

const getWaitingPeriodContent = (
  item: ProductClass,
  setProductClassModalState: (state: ProductClassModalStateProps) => void,
  translate: (key: string) => string,
): ReactNode => {
  return (
    <Col span={24} className="class_section p-8 pt-24">
      <div className="class_section_header border_bottom">
        <h6 className="title">
          {translate('admin-dashboard.benefit-details.summary.class-waiting-period')}:{' '}
          {item.waitingPeriod === null
            ? ''
            : item.waitingPeriod === 0
            ? translate('admin-dashboard.benefit-details.summary.no-waiting-period')
            : `${translate('admin-dashboard.benefit-details.summary.yes-waiting-period')}, 
                      ${item.waitingPeriod}-${translate(
                'admin-dashboard.benefit-details.summary.yes-day-waiting-period',
              )}`}
        </h6>
        {waitingPeriodHeaderValue(item, setProductClassModalState, translate)}
      </div>
    </Col>
  )
}

const getProrateAllowanceContent = (
  item: ProductClass,
  setProductClassModalState: (state: ProductClassModalStateProps) => void,
  translate: (key: string) => string,
): ReactNode => {
  return (
    <Col span={24} className="class_section p-8 pt-24">
      <div className="class_section_header border_bottom">
        <h6 className="title">{translate('admin-dashboard.benefit-details.summary.labels.prorate-allowances')}</h6>
        {prorateAllowanceHeaderValue(item, setProductClassModalState, translate)}
      </div>
    </Col>
  )
}

const prorateAllowanceHeaderValue = (
  item: ProductClass,
  setProductClassModalState: (state: ProductClassModalStateProps) => void,
  translate: (key: string) => string,
): ReactNode => {
  return editIcon(setProductClassModalState, item.id, ProductClassStep.ProrateAllowances)
}

const waitingPeriodHeaderValue = (
  item: ProductClass,
  setProductClassModalState: (state: ProductClassModalStateProps) => void,
  translate: (key: string) => string,
): ReactNode => {
  if (item.waitingPeriod === null) return notSetHeaderValue(translate)

  return editIcon(setProductClassModalState, item.id, ProductClassStep.WaitingPeriod)
}

const notSetHeaderValue = (translate: (key: string) => string) => (
  <h6 className="not_set">{translate('general.not-set')}</h6>
)

const setModalState = (
  setProductClassModalState: (state: ProductClassModalStateProps) => void,
  productClassId: string | null,
  step: ProductClassStep,
): void => {
  setProductClassModalState({
    visible: true,
    editingStep: step,
    productId: productClassId !== null ? productClassId : undefined,
  })
}
